import { axiosInstance } from "../axios/axiosConfig";
import { v4 as uuidv4 } from "uuid";
import { Observable, from, mergeMap, toArray, catchError, tap } from "rxjs";

const baseAPIUrl = "/upload";

const CHUNK_SIZE = 10 * 1024 * 1024;

export const uploadFile = (file) => {
  const url = `${baseAPIUrl}`;
  const formData = new FormData();
  formData.append("input.FormFile", file);

  return axiosInstance.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }).then((resp) => resp.data);
};

const uploadChunkRequest = (formData) => {
  const url = `${baseAPIUrl}/UploadChunkFile`;
  return axiosInstance.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }).then((resp) => resp.data);
};

const uploadChunkRequestWithLoading = (formData, currentChunk) => {
  const url = `${baseAPIUrl}/UploadChunkFile`;
  return axiosInstance.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }).then((resp) => resp.data);
};

const completeUpload = (fileId, fileName, contentType) => {
  const url = `${baseAPIUrl}/CompletedUploadChunkFile`;
  const formData = new FormData();
  formData.append("fileId", fileId);
  formData.append("fileName", fileName);
  formData.append("contentType", contentType);

  return axiosInstance.post(url, formData).then((resp) => resp.data);
};

const completeUploadWithLoading = (fileId, fileName, contentType) => {
  const url = `${baseAPIUrl}/CompletedUploadChunkFile`;
  const formData = new FormData();
  formData.append("fileId", fileId);
  formData.append("fileName", fileName);
  formData.append("contentType", contentType);

  return axiosInstance.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }).then((resp) => resp.data);
};

export const uploadFileInChunks = (file, concurrentNumber = 10, withLoading = true) => {
  return new Observable((observer) => {
    const fileSize = file.size;
    const totalChunks = Math.ceil(fileSize / CHUNK_SIZE);
    const fileId = `${uuidv4()}_${Date.now()}`;
    let completedChunks = 0;
    
    function createUploadChunkObservable(req, observer, totalChunks, fileId, fileName) {
      return from(req).pipe(
        tap(() => {
          completedChunks++;
          const overallProgress = Math.round((completedChunks / totalChunks) * 100);
          observer.next({ progress: overallProgress, fileId, fileName });
        }),
        catchError((error) => {
          observer.error(error);
          return [];
        })
      );
    }
    
    const chunkRequests = [];
    
    for (let currentChunk = 0; currentChunk < totalChunks; currentChunk++) {
      const start = currentChunk * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, fileSize);
      const chunkFile = file.slice(start, end);
    
      const formData = new FormData();
      formData.append("chunkFile.FormFile", chunkFile);
      formData.append("fileId", fileId);
      formData.append("chunkIndex", currentChunk.toString());
    
      let req;
      if (withLoading) {
        req = uploadChunkRequestWithLoading(formData, currentChunk);
      } else {
        req = uploadChunkRequest(formData);
      }
    
      const uploadChunk$ = createUploadChunkObservable(req, observer, totalChunks, fileId, file.name);
      chunkRequests.push(uploadChunk$);
    }

    from(chunkRequests)
      .pipe(
        mergeMap((chunkRequest) => chunkRequest, concurrentNumber),
        toArray()
      )
      .subscribe({
        next: () => {
          if (withLoading) {
            completeUploadWithLoading(fileId, file.name, file.type)
              .then((response) => {
                observer.next({
                  progress: 100,
                  fileId,
                  fileName: file.name,
                  response: response,
                });
                observer.complete();
              })
              .catch((err) => {
                observer.error(err);
              });
          } else {
            completeUpload(fileId, file.name, file.type)
              .then((response) => {
                observer.next({
                  progress: 100,
                  fileId,
                  fileName: file.name,
                  response: response,
                });
                observer.complete();
              })
              .catch((err) => {
                observer.error(err);
              });
          }
        },
        error: (err) => {
          observer.error(err);
        },
      });
  });
};
